import type { Table, Updater } from '@tanstack/react-table';
import type { Virtualizer } from '@tanstack/react-virtual';

import type { TableState } from '../../shared/hooks/useTableState';

export interface UseTableStateHandlersOptions {
  tableState: TableState | undefined;
  initialState: TableState;
  updateState: (updates: Partial<TableState>) => void;
  table: Table<any>;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
}

export interface UseTableStateHandlersReturn {
  onColumnFiltersChange: (updater: Updater<TableState['columnFilters']>) => void;
  onColumnVisibilityChange: (updater: Updater<TableState['columnVisibility']>) => void;
  onGlobalFilterChange: (updater: Updater<TableState['globalFilter']>) => void;
  onSortingChange: (updater: Updater<TableState['sorting']>) => void;
}

export function createStateHandler<K extends keyof TableState>(
  stateKey: K,
  tableState: TableState | undefined,
  initialState: TableState,
  updateState: (updates: Partial<TableState>) => void,
  rowVirtualizer?: Virtualizer<HTMLDivElement, Element>,
  table?: Table<any>,
) {
  return (updater: Updater<TableState[K]>) => {
    const currentValue = tableState?.[stateKey] ?? initialState[stateKey];
    const newValue = typeof updater === 'function' ? updater(currentValue) : updater;
    updateState({ [stateKey]: newValue } as Partial<TableState>);
    if (stateKey === 'sorting' || stateKey === 'columnFilters' || stateKey === 'globalFilter') {
      if (!!table?.getRowModel().rows.length && rowVirtualizer) {
        rowVirtualizer.scrollToIndex?.(0);
      }
    }
  };
}

export function useTableStateHandlers({
  tableState,
  initialState,
  updateState,
  rowVirtualizer,
  table,
}: UseTableStateHandlersOptions): UseTableStateHandlersReturn {
  const onColumnFiltersChange = createStateHandler(
    'columnFilters',
    tableState,
    initialState,
    updateState,
    rowVirtualizer,
    table,
  );

  const onColumnVisibilityChange = createStateHandler(
    'columnVisibility',
    tableState,
    initialState,
    updateState,
    rowVirtualizer,
    table,
  );
  const onGlobalFilterChange = createStateHandler(
    'globalFilter',
    tableState,
    initialState,
    updateState,
    rowVirtualizer,
    table,
  );
  const onSortingChange = createStateHandler('sorting', tableState, initialState, updateState, rowVirtualizer, table);

  return {
    onColumnFiltersChange,
    onColumnVisibilityChange,
    onGlobalFilterChange,
    onSortingChange,
  };
}
